/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description ExportClaimStatusData button
 */
import React, { useMemo } from 'react';
import _ from 'lodash';
import { Download } from '@icon-park/react';
import { Button, Modal, Row, Col, Tag, Tooltip, Space, Spin } from 'antd';
import { JsonRule } from '@react-awesome-query-builder/antd';
import { LoadingOutlined } from '@ant-design/icons';

import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import {
	tagStyle,
	headerTextStyle,
	paginationParams,
	DownloadWarning,
	ServiceDateOptions,
	MAX_EXPORT_ITEMS,
	getAllFilterKeysFromQuery,
} from './ExportEligibilityData';
import { filterDataKeys, getFilterParamsWithServiceDate } from 'components/forms/schema/claimsAdvancedFilterSchema';
import useAllClaimStatusRequests, { addClaimRequestScopeRules } from 'hooks/claim/useAllClaimStatusRequests';
import useExportClaimStatusData from 'hooks/claim/mutate/useExportClaimStatusData';
import { useClaimStatusRequestsStoreActions, useClaimStatusRequestsStoreValues } from 'store/claimsRequests';
import { dataKeyLabels } from 'utils/claims/claimStatusRequests';
import { addRuleToQuery, prepareQueryRule } from 'utils/queryBuilder';

const getClaimIdsRule = (claimIds: string[]): JsonRule => {
	return prepareQueryRule({
		field: filterDataKeys.claimId,
		operator: OPERATORS.multiselectContains,
		value: [claimIds],
		valueType: ['text'],
	});
};

export interface IProps {
	claimStatusIDs?: string[];
	workStateGroup?: ClaimRequest.IGroupedClaimWorkState;
	batchPostPaymentMeta?: {
		batchId: string;
		status: PatientPayment.IPostPaymentGroupedStatus;
	};
}
/**
 * ExportClaimStatusData button
 */
const ExportClaimStatusData: React.FC<IProps> = ({ claimStatusIDs, workStateGroup, batchPostPaymentMeta }) => {
	const { apiParams, exportMetaData } = useClaimStatusRequestsStoreValues();
	const { setExportMetaData, closeExportModal } = useClaimStatusRequestsStoreActions();
	const { mutate, isPending } = useExportClaimStatusData();
	const isClaimStatusIDs = !!_.size(claimStatusIDs);

	// function to prepare initial data from api params
	const { filterParams, fieldValuesSet, hasFilterParams } = useMemo(() => {
		const params = _.pickBy(_.cloneDeep(apiParams), (value) => !_.isEmpty(value));
		const allFieldValues = _.uniq(getAllFilterKeysFromQuery(apiParams));
		return {
			filterParams: params,
			fieldValuesSet: allFieldValues,
			hasFilterParams: !_.isEmpty(allFieldValues),
		};
	}, [apiParams]);

	const filterParamsWithServiceDate = useMemo(
		() => getFilterParamsWithServiceDate(exportMetaData?.dateRange?.range),
		[exportMetaData?.dateRange?.range],
	);

	// function to  prepare search params
	const searchParam = useMemo(() => {
		const base = {
			...(hasFilterParams && filterParams),
			...(!hasFilterParams && {
				criteria: isClaimStatusIDs
					? addRuleToQuery(filterParamsWithServiceDate, getClaimIdsRule(claimStatusIDs))
					: filterParamsWithServiceDate,
			}),
		};
		// scope export to the same work state / batch post payment as the table (mirrors useAllClaimStatusRequests)
		return { ...base, criteria: addClaimRequestScopeRules(base.criteria, { workStateGroup, batchPostPaymentMeta }) };
	}, [filterParams, isClaimStatusIDs, filterParamsWithServiceDate, workStateGroup, batchPostPaymentMeta]);

	const {
		data: claimData,
		isLoading,
		isFetching,
	} = useAllClaimStatusRequests(
		{
			apiParams: searchParam,
			pageNumber: paginationParams.pageNumber,
			pageSize: paginationParams.pageSize,
		},
		{ enabled: exportMetaData.visible && !!exportMetaData.dateRange?.range },
	);
	const claimsCount = claimData?.count && exportMetaData.dateRange?.range ? claimData?.count : 0;
	const isLimitExceed = claimsCount > MAX_EXPORT_ITEMS;

	const exportData = () => {
		closeExportModal();
		mutate({ searchParam });
	};

	// function to render filter tags
	const renderFilterTags = useMemo(() => {
		return (
			<Col span={24}>
				<Row className='flex'>
					<Col>
						<span style={headerTextStyle}>{'Filters Applied: '}</span>
					</Col>
					<Col className='ml-1.5'>
						{_.map(fieldValuesSet, (values, key) => {
							const displayedKey = _.get(dataKeyLabels, values) || key;
							return (
								<Tag style={tagStyle}>
									{displayedKey} {_.isArray(values) ? ` (${_.size(values)})` : ''}
								</Tag>
							);
						})}
					</Col>
				</Row>
			</Col>
		);
	}, [filterParams]);

	return (
		<>
			<Tooltip title={'Download Responses'}>
				<Button
					color='default'
					loading={isPending}
					onClick={() => setExportMetaData({ ...exportMetaData, visible: true })}
					icon={<Download />}
				/>
			</Tooltip>
			<Modal
				open={exportMetaData?.visible}
				closable={true}
				maskClosable={false}
				width={450}
				title={'Download Responses'}
				okText={`Download Responses (${claimsCount})`}
				onOk={exportData}
				onCancel={closeExportModal}
				cancelButtonProps={{ disabled: isPending }}
				okButtonProps={{
					loading: isPending,
					disabled: isLoading || isFetching || _.isEmpty(exportMetaData.dateRange?.range) || claimsCount < 1,
				}}
			>
				{exportMetaData?.visible && (
					<Row gutter={[0, 10]}>
						{hasFilterParams && renderFilterTags}
						{!hasFilterParams && (
							<ServiceDateOptions
								exportMetaData={exportMetaData}
								updateDateRange={(dateRange) => setExportMetaData({ ...exportMetaData, dateRange })}
							/>
						)}
						{isLimitExceed && <DownloadWarning />}
						{(isLoading || isFetching) && (
							<Space>
								<Spin spinning indicator={<LoadingOutlined />} />
								{'Kindly wait while we process your request.'}
							</Space>
						)}
					</Row>
				)}
			</Modal>
		</>
	);
};

export default ExportClaimStatusData;
export { getClaimIdsRule };
